import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMessageSquare, FiPlus, FiX, FiCalendar, FiUser, FiTrash2, FiSearch, FiBriefcase, FiChevronDown } = FiIcons;

const CommunicationLog = () => {
  const { communicationLogs, students, parents, addCommunicationLog, deleteCommunicationLog } = useData();
  const { user } = useAuth();
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    studentId: '',
    type: 'Portal Message',
    subject: '',
    notes: '',
    tutorName: '',
    date: new Date().toISOString().split('T')[0]
  });

  const channels = ['Phone', 'Email', 'WhatsApp', 'In-Person', 'Portal Message'];

  const parentRecord = parents.find(p => p.id === user?.uid || p.id === user?.id || p.email?.toLowerCase() === user.email?.toLowerCase());
  const portalStudents = user.role === 'parent' 
    ? students.filter(s => s.parentId === parentRecord?.id)
    : students;

  const filteredLogs = communicationLogs
    .filter(log => {
      if (user.role === 'parent') {
        const myChildrenIds = portalStudents.map(s => s.id);
        if (!myChildrenIds.includes(log.studentId)) return false;
      }
      const search = searchTerm.toLowerCase();
      const student = students.find(s => s.id === log.studentId);
      return (
        log.subject.toLowerCase().includes(search) ||
        student?.studentName.toLowerCase().includes(search) ||
        log.notes.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const getAvailableTutorsForStudent = (studentId) => {
    const student = students.find(s => s.id === studentId);
    if (!student) return [];
    const tutors = new Set();
    if (student.subjects && Array.isArray(student.subjects)) {
      student.subjects.forEach(s => s.tutor && tutors.add(s.tutor));
    }
    if (student.tutor) tutors.add(student.tutor);
    return Array.from(tutors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await addCommunicationLog({
        ...formData,
        createdBy: user.name || `${user.firstName} ${user.lastName}`,
        createdByUid: user.uid || user.id,
        createdByRole: user.role
      });
      setShowAddForm(false);
      setFormData({ studentId: '', type: 'Portal Message', subject: '', notes: '', tutorName: '', date: new Date().toISOString().split('T')[0] });
    } catch (err) {
      alert("Failed to save log");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h2 className="text-4xl font-black text-gray-900 tracking-tight">Comm Log</h2>
          <p className="text-sm font-bold text-gray-400 mt-1 uppercase tracking-widest">Interaction History</p>
        </div>
        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setShowAddForm(true)} className="flex items-center gap-3 px-8 py-4 bg-indigo-600 text-white rounded-[1.5rem] font-black text-xs uppercase tracking-[0.2em] shadow-2xl shadow-indigo-100">
          <SafeIcon icon={FiPlus} /> Log Interaction
        </motion.button>
      </div>

      <div className="bg-white p-4 rounded-[2.5rem] shadow-sm border border-gray-100">
        <div className="relative">
          <SafeIcon icon={FiSearch} className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search interactions..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-14 pr-6 py-4 bg-gray-50 border-2 border-transparent rounded-[1.8rem] focus:bg-white focus:border-indigo-600 outline-none transition-all font-bold text-gray-700" />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <AnimatePresence mode="popLayout">
          {filteredLogs.map((log) => {
            const student = students.find(s => s.id === log.studentId);
            return (
              <motion.div layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} key={log.id} className="bg-white p-8 rounded-[2rem] shadow-sm border-2 border-transparent hover:border-indigo-50 transition-all flex flex-col md:flex-row gap-8 items-start">
                <div className="w-full md:w-48 flex-shrink-0">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                      <SafeIcon icon={FiMessageSquare} />
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-widest text-indigo-600">{log.type}</span>
                  </div>
                  <p className="text-sm font-black text-gray-900">{new Date(log.date).toLocaleDateString(undefined, { dateStyle: 'medium' })}</p>
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-black text-gray-900 tracking-tight">{log.subject}</h3>
                      <div className="flex flex-wrap gap-4 mt-2">
                        <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-[9px] font-black uppercase tracking-widest">Student: {student?.studentName || 'Unknown'}</span>
                        {log.tutorName && <span className="px-3 py-1 bg-amber-50 text-amber-600 rounded-lg text-[9px] font-black uppercase tracking-widest flex items-center gap-1"><SafeIcon icon={FiBriefcase} /> Tutor: {log.tutorName}</span>}
                      </div>
                    </div>
                    {(user.role === 'admin' || user.uid === log.createdByUid) && (
                      <button onClick={() => deleteCommunicationLog(log.id)} className="p-2 text-rose-200 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all">
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    )}
                  </div>
                  <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
                    <p className="text-gray-600 font-medium whitespace-pre-wrap">{log.notes}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {showAddForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-[2.5rem] shadow-2xl max-w-2xl w-full overflow-hidden">
            <div className="flex items-center justify-between p-8 border-b border-gray-100">
              <h2 className="text-2xl font-black text-gray-900 tracking-tight">Log Interaction</h2>
              <button onClick={() => setShowAddForm(false)} className="p-2 text-gray-400"><SafeIcon icon={FiX} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Student</label>
                  <select value={formData.studentId} onChange={(e) => setFormData({ ...formData, studentId: e.target.value, tutorName: '' })} className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none font-bold appearance-none" required>
                    <option value="">Select Child...</option>
                    {portalStudents.map(s => <option key={s.id} value={s.id}>{s.studentName}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Assign to Tutor</label>
                  <select value={formData.tutorName} onChange={(e) => setFormData({ ...formData, tutorName: e.target.value })} className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none font-bold appearance-none" required disabled={!formData.studentId}>
                    <option value="">Choose Tutor...</option>
                    {getAvailableTutorsForStudent(formData.studentId).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Subject</label>
                  <input type="text" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none font-bold" placeholder="e.g. Weekly Progress Check" required />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Channel</label>
                  <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none font-bold appearance-none">
                    {channels.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2 space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Conversation Notes</label>
                  <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none font-bold min-h-[120px]" placeholder="Summary of what was discussed..." required />
                </div>
              </div>
              <div className="flex justify-end gap-4 pt-6 border-t font-black">
                <button type="button" onClick={() => setShowAddForm(false)} className="px-6 text-gray-400 text-xs uppercase tracking-widest">Cancel</button>
                <button type="submit" disabled={loading} className="px-10 py-4 bg-indigo-600 text-white rounded-2xl text-xs uppercase tracking-widest shadow-xl"> {loading ? 'Saving...' : 'Save Entry'} </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default CommunicationLog;